import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MEDDICScores, Opportunity } from '@vendasta/sales-opportunities';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Salesperson } from '@galaxy/types';
import { ALL_SALESPEOPLE, DEFAULT_CURRENCY, DETAILS_CONFIG_TOKEN } from './tokens';
import { PipelineBoardService } from '../pipeline-board-service/pipeline-board.service';
import { SortSelection } from '../pipeline-board-service/interfaces';
import { PipelineBusinessNameService } from '../pipeline-business-name/pipeline-business-name.service';
import { MEDDICScoreSource } from '../../meddic/meddic-scores/m-e-d-d-i-c-score-source';
import { SlideOutPanelService } from '../../slide-out-panel/slide-out-panel.service';
import { DetailsConfig } from '../../sales-opportunities/opportunity';
import { OpportunityService } from '../../sales-opportunities/opportunity.service';
import { Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  selector: 'sales-ui-pipeline-card',
  templateUrl: './pipeline-card.component.html',
  styleUrls: ['./pipeline-card.component.scss'],
})
export class PipelineCardComponent implements OnInit {
  @Input() opportunity: Opportunity;
  @Output() selectedOpportunityId = new EventEmitter<string>();

  readonly sortSelection$: Observable<SortSelection>;
  readonly currency$: Observable<string> = this.defaultCurrency$;

  businessName$: Observable<string>;
  error$: Observable<boolean>;
  isSelectedOpportunity$: Observable<boolean>;
  salesperson$: Observable<Salesperson>;
  nameLoading$ = new BehaviorSubject<boolean>(false);
  meddicScore = 0;
  MEDDICScores$: Observable<MEDDICScores>;
  MEDDICScoresLoading$: Observable<boolean>;

  constructor(
    private readonly pipelineBoardService: PipelineBoardService,
    private readonly pipelineBusinessNameService: PipelineBusinessNameService,
    private readonly opportunityUpdates: OpportunityService,
    private readonly scoreSvc: MEDDICScoreSource,
    private readonly slideOutPanelService: SlideOutPanelService,
    @Inject(ALL_SALESPEOPLE) private readonly salespeople$: Observable<Salesperson[]>,
    @Inject(DEFAULT_CURRENCY) private readonly defaultCurrency$: Observable<string>,
    @Inject(DETAILS_CONFIG_TOKEN) private readonly detailsConfig: DetailsConfig,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
  ) {
    this.sortSelection$ = this.pipelineBoardService.sortSelection$;

    this.isSelectedOpportunity$ = combineLatest([
      this.opportunityUpdates.currentOpportunity$,
      this.slideOutPanelService.isSlideOutOpen$,
    ]).pipe(
      map(([selectedOpportunity, isSlideOutOpen]) => {
        const isOpportunitySelected = selectedOpportunity
          ? this.opportunity.opportunityId === selectedOpportunity.opportunityId
          : false;
        return isOpportunitySelected === true && isSlideOutOpen === true;
      }),
    );
  }

  ngOnInit(): void {
    const accountGroupId = this.opportunity.accountGroupId;
    this.setBusinessName(this.opportunity, this.detailsConfig);

    this.nameLoading$ = this.pipelineBusinessNameService.businessIdentifiers.get(
      this.opportunity.accountGroupId,
    ).loading$$;

    this.salesperson$ = this.getSalesperson(this.opportunity.salesPersonId);

    this.meddicScore =
      this.opportunity.scoring?.scores.reduce((total, score) => {
        const currentScore = score?.scoreValue || 0;
        return total + currentScore;
      }, 0) ?? 0;

    this.MEDDICScores$ = this.scoreSvc.getCachedScoresOrFetch$(accountGroupId, this.opportunity.opportunityId);
    this.MEDDICScoresLoading$ = this.scoreSvc.isLoading$(this.opportunity.opportunityId);
  }

  getBusinessName$(accountGroupId: string): Observable<string> {
    const businessObject = this.pipelineBusinessNameService.businessIdentifiers.get(accountGroupId);
    return businessObject.accountGroupName$$;
  }

  getBusinessNameError$(accountGroupId: string): Observable<boolean> {
    const businessObject = this.pipelineBusinessNameService.businessIdentifiers.get(accountGroupId);
    return businessObject.accountGroupName$$.pipe(
      map((bn) => {
        return bn === '';
      }),
    );
  }

  getDateOrNull(date: Date): string | null {
    return date.getFullYear() > 1969 ? date.toLocaleDateString() : null;
  }

  selected(): void {
    this.selectedOpportunityId.emit(this.opportunity.opportunityId);
  }

  getSalesperson(salespersonId: string): Observable<Salesperson> {
    return this.salespeople$.pipe(map((sp) => sp.find((s) => s.id === salespersonId)));
  }

  setBusinessName(opportunity: Opportunity, details: DetailsConfig): void {
    if (details.showCRMDetails) {
      this.businessName$ = this.pipelineBusinessNameService.getCompanyName$(opportunity.accountGroupId);
    } else {
      this.businessName$ = this.getBusinessName$(opportunity.accountGroupId);
    }
  }

  async openDetails(opportunity: Opportunity, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    if (!opportunity.accountGroupId) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.TABLE.ERRORS.NAVIGATE_TO_PAGE');
      return;
    }
    if (this.detailsConfig.showCRMDetails) {
      const url = await firstValueFrom(
        this.pipelineBusinessNameService
          .getCompanyId$(opportunity.accountGroupId)
          .pipe(map((id) => this.detailsConfig.getCompanyDetailsUrl(id))),
      );
      this.router.navigate([url], { queryParams: { origin: 'pipeline-card' } });
    } else {
      const url = this.detailsConfig.getAccountDetailsUrl(opportunity);
      this.router.navigate([url], { queryParams: { origin: 'pipeline-card' } });
    }
  }
}
